// Resolves the schoolId for shortcut / legacy routes (URLs without :schoolId in the path).
//
// Canonical routes always carry :schoolId — this helper only exists for entry points that
// don't: the `/painel` marketing link, and legacy bookmarks like `/quizzes` that predate the
// canonical routing. In both cases we pick the "most likely intended" school by falling back
// through a small ladder:
//
//   1. localStorage last-used — the school the user was last looking at in this browser;
//   2. auth.user.currentSchool — whichever the backend session currently has selected;
//   3. auth.user.schools[0] — first of the user's scope, deterministic fallback.
//
// If none of the above lands on a school still in scope, we return null so the caller can send
// the user back to /login instead of a URL that would 403. That guarantees the resolution
// is always against the CURRENT scope, never a stale id from a school the user lost access to.

const LS_KEY = 'reeduca:lastSchoolId';

// Returns the localStorage-remembered schoolId iff it's still in the user's scope, otherwise null.
// Kept as a standalone export because callers that only care about the "remembered" hop (not the
// fallback ladder) can query it directly — e.g. code that wants to distinguish "user has a
// preference" from "picked deterministically".
export function getLastSchoolIdInScope(user) {
	const scope = (user && Array.isArray(user.schools)) ? user.schools : [];
	if (scope.length === 0) return null;

	try {
		const last = window.localStorage.getItem(LS_KEY);
		if (last && scope.some(s => s.id === last)) return last;
	} catch (e) {
		// Private mode, storage disabled, etc.
	}
	return null;
}

// Takes a user object (auth.user, or response.userData shape from login) — not the auth wrapper.
// Callers that only have `auth` in scope should pass `auth.user`; callers that already hold the
// user (LoginPage, Sidebar after selectSchool resolves) pass it directly.
export function resolveSchoolId(user) {
	const scope = (user && Array.isArray(user.schools)) ? user.schools : [];
	if (scope.length === 0) return null;

	// 1. localStorage last-used — only trusted if still in scope
	const remembered = getLastSchoolIdInScope(user);
	if (remembered) return remembered;

	const scopeIds = new Set(scope.map(s => s.id));

	// 2. current selected school on the user object (session preference)
	const current = user && user.currentSchool && user.currentSchool.id;
	if (current && scopeIds.has(current)) return current;

	// 3. deterministic fallback
	return scope[0].id;
}

// Replaces `:schoolId` in a route path template with a concrete id — for callers that pull the
// canonical path from PrivateRoutesConfig and need a navigable URL (getHomeBrowserRoute, etc.).
export function substituteSchoolId(path, schoolId) {
	if (!path || !schoolId) return path;
	return path.replace(':schoolId', schoolId);
}

export function setLastSchoolId(schoolId) {
	if (!schoolId) return;
	try {
		window.localStorage.setItem(LS_KEY, schoolId);
	} catch (e) {
		// Best-effort — next resolveSchoolId call will fall back gracefully.
	}
}
